import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import robloxApi from '../../api/robloxApi';
import { AssetInterface } from "../../store/roblox/assetsSlice";
import assetMakerUrls from "../../configs/roblox/robloxUrls";

const PREFIX = 'roblox';

const processStrategy = (value: any) => {
  return {
    ...value,
    item_url: assetMakerUrls.remoteUrl(value.item_url)
  };
}

export const assetsEntity = new schema.Entity(
  'assets',
  {},
  {
    processStrategy
  }
);

export const assetEntity = new schema.Entity(
  'asset',
  {},
  {
    processStrategy
  }
);

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      asset: AssetInterface
    }
  >(response.data.asset, assetEntity);
  return normalized.entities;
}

const normalizeUploadInstance = (response: any) => {
  const normalized = normalize<
    any,
    {
      asset: AssetInterface
    }
    >(response.asset, assetEntity);
  return normalized.entities;
}

const normalizeCollection = (response: AxiosResponse) => {
  const normalized  = normalize<
    any,
    {
      assets: { [key: string]: AssetInterface },
    }
    >(response.data.assets, [assetsEntity]);

  return {
    assets: normalized.entities?.assets || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

export const fetchAssets = createAsyncThunk(
  `${PREFIX}_assets/fetchAssets`,
  async (params: object = {}) => {
    const response = await robloxApi.get(`/onesky/assets`, { params })
    return normalizeCollection(response);
  }
);

export const uploadAsset = createAsyncThunk(
  `${PREFIX}_assets/createAsset`,
  async (response: object, { rejectWithValue }) => {
    try {
      return normalizeUploadInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const createAsset = createAsyncThunk(
  `${PREFIX}_assets/createAsset`,
  async (data: object, { rejectWithValue }) => {
    try {
      const response = await robloxApi.post(`/onesky/assets`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const fetchAsset = createAsyncThunk(
  `${PREFIX}_assets/fetchAsset`,
  async (id: number | string) => {
    const response = await robloxApi.get(`/onesky/assets/${id}`);
    return normalizeInstance(response);
  }
);

export const updateAsset = createAsyncThunk(
  `${PREFIX}_assets/updateAsset`,
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await robloxApi.patch(`/onesky/assets/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyAsset = createAsyncThunk(
  `${PREFIX}_assets/destroyAsset`,
  async (id: number) => {
    await robloxApi.delete(`/onesky/assets/${id}`);
    return { id: id };
  }
);
